import { useCallback } from "react";
import { Link } from "react-router-dom";
import { Box, CSSObject, MantineTheme } from "@mantine/core";
import { type Row } from "@tanstack/react-table";
import { RoutePath } from "src/routes";
import { buildUrl, RoutePathParams } from "src/utils/buildUrl";

import { ContextMenu, ContextMenuItem } from "../../ContextMenu";
import TableCell from "../TableCell";

import styles from "./TableRow.module.scss";

interface ActionButtonBase {
    icon: React.ReactNode;
    label: string;
}

interface ActionButton<T> extends ActionButtonBase {
    type: "button";
    onClick: (row: T) => void;
}

export interface ActionLink<T> extends ActionButtonBase {
    type: "link";
    getRoutePathParams: (row: T) => RoutePathParams<RoutePath>;
}

export type ActionButtonType<T> = ActionButton<T> | ActionLink<T>;

export interface TableRowProps<Data> {
    row: Row<Data>;
    index: number;
    getRowHref?: (row: Row<Data>) => RoutePathParams<RoutePath>;
    onRowClick?: (row: Row<Data>) => void;
    onRowDoubleClickCapture?: (row: Row<Data>) => void;
    getCellStyles?: ({ row, column }: { row: Row<Data>; column: { id: string } }) => string[];
    getContextMenuItems?: (row: Row<Data>) => ContextMenuItem[];
    showContextMenu: (
        menuFn: (onClose: () => void) => JSX.Element,
        config?: Record<string, unknown>
    ) => (event: React.MouseEvent) => void;
}

const getBoxSx = (theme: MantineTheme): CSSObject => {
    const backgroundColor = theme.colorScheme === "dark" ? theme.colors.dark[5] : theme.colors.gray[0];
    const hoverBackgroundColor = theme.colorScheme === "dark" ? theme.colors.dark[6] : theme.colors.gray[1];

    return {
        borderTopWidth: "1px",
        borderTopStyle: "solid",
        borderTopColor: theme.colorScheme === "dark" ? theme.colors.gray[8] : theme.colors.gray[3],
        backgroundColor,
        "&:hover": {
            backgroundColor: hoverBackgroundColor,
        },
    };
};

const TableRow = <Data extends object>({
    row,
    index,
    getRowHref,
    onRowClick,
    onRowDoubleClickCapture,
    getCellStyles,
    getContextMenuItems,
    showContextMenu,
}: TableRowProps<Data>) => {
    const rowHref = getRowHref?.(row);

    const handleRowClick = useCallback(() => {
        onRowClick?.(row);
    }, [onRowClick, row]);

    const handleRowDoubleClick = useCallback(() => {
        onRowDoubleClickCapture?.(row);
    }, [onRowDoubleClickCapture, row]);

    const contextMenuHandler = useCallback(() => {
        if (!getContextMenuItems) {
            return undefined;
        }

        return showContextMenu((onClose) => <ContextMenu items={getContextMenuItems(row)} onClose={onClose} />, {
            style: {
                border: "none",
                backgroundColor: "transparent",
            },
        });
    }, [getContextMenuItems, showContextMenu, row]);

    const rowContent = (
        <Box
            key={`${index}${row.id}`}
            component="tr"
            onContextMenu={contextMenuHandler}
            className={styles.box}
            sx={getBoxSx}
        >
            {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id} cell={cell} getCellStyles={getCellStyles} />
            ))}
        </Box>
    );

    if (rowHref) {
        return (
            <Link to={buildUrl(rowHref.path, rowHref.params)} className={styles.row}>
                {rowContent}
            </Link>
        );
    }

    if (onRowClick) {
        return (
            <button type="button" onClick={handleRowClick} onDoubleClick={handleRowDoubleClick} className={styles.row}>
                {rowContent}
            </button>
        );
    }

    return <div className={styles.row}>{rowContent}</div>;
};

export default TableRow;
